import { useEffect, useRef, useState } from 'react'
import { Property } from '../types/property'

interface MapPanelProps {
  properties: Property[]
  selectedProperty: Property | null
  onPropertySelect: (property: Property) => void
  onBoundsChange: (bounds: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } }) => void
}

declare global {
  interface Window {
    google: typeof google
  }
}

export default function MapPanel({
  properties,
  selectedProperty,
  onPropertySelect,
  onBoundsChange,
}: MapPanelProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  // Wait for Google Maps to load
  useEffect(() => {
    const checkGoogleMaps = setInterval(() => {
      if (window.google?.maps) {
        setIsLoaded(true)
        clearInterval(checkGoogleMaps)
      }
    }, 100)

    return () => clearInterval(checkGoogleMaps)
  }, [])

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 39.7392, lng: -104.9903 }, // Denver
      zoom: 11,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
    })

    mapInstanceRef.current = map

    // Add bounds change listener
    map.addListener('idle', () => {
      const bounds = map.getBounds()
      if (bounds) {
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()
        onBoundsChange({
          ne: { lat: ne.lat(), lng: ne.lng() },
          sw: { lat: sw.lat(), lng: sw.lng() },
        })
      }
    })
  }, [isLoaded, onBoundsChange])

  // Update markers when properties change
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null))
    markersRef.current = []

    // Add new markers
    const bounds = new window.google.maps.LatLngBounds()
    let boundsExtended = false

    properties.forEach(property => {
      if (!property.lat || !property.lng) return

      const position = { lat: property.lat, lng: property.lng }

      // Create custom marker with price label
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current!,
        title: property.address,
        label: {
          text: `$${(property.price / 1000).toFixed(0)}k`,
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: selectedProperty?.listingKey === property.listingKey ? '#000000' : '#4F46E5',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2,
        },
      })

      marker.addListener('click', () => {
        onPropertySelect(property)
      })

      markersRef.current.push(marker)
      bounds.extend(position)
      boundsExtended = true
    })

    // Fit bounds if we have properties
    if (boundsExtended && properties.length > 0) {
      mapInstanceRef.current.fitBounds(bounds)
    }
  }, [properties, selectedProperty, isLoaded, onPropertySelect])

  // Pan to selected property
  useEffect(() => {
    if (selectedProperty && mapInstanceRef.current) {
      mapInstanceRef.current.panTo({
        lat: selectedProperty.lat,
        lng: selectedProperty.lng,
      })
    }
  }, [selectedProperty])

  return (
    <div className="w-full h-full relative">
      <div ref={mapRef} className="w-full h-full" />
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  )
}
